import produce from 'immer';
import {Dispatch, Reducer, ReducerAction, useEffect, useMemo, useReducer} from 'react';

import {assert, assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {FullyAccessibleGrid} from 'constants/SampleConstants';
import {StudyDesignType} from 'graphql-client/__generated/graphqlGlobalTypes';
import NavStore from 'nav/NavStore';
import {getInitialRegistry} from 'registry/DefaultRegistry';
import {CreatePlatePaneState, DEFAULT_CREATE_PLATE_PANE_STATE} from 'samples/CreatePlatePane';
import {usePlateMapFromGrid} from 'samples/grids/hooks/usePlateMapFromGrid';
import {
  ANNOTATE_FILL_PLATE_MODAL_STEPS,
  AnnotateFillPlateModalStep,
  DefineExistingPlateState,
  PlateDefinitionType,
  PlateDesignState,
  PlateMapType,
} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import {PlateDataParameter, PlateMap} from 'samples/plate-maps/PlateMapConstants';
import {
  getDataParameterNamesByVersionId,
  getEmptyPlateMap,
  getInitialDataParameterTags,
  getInitialSelectedDataParameters,
  shouldShowTestOrderDilutionInfo,
} from 'samples/plate-maps/PlateMapUtil';
import {NO_MATERIAL_INPUT_SENTINEL} from 'samples/plate-maps/transfer-table/FillPlateTransferTableConstants';
import {
  filterGroupedValidation,
  filterValidation,
  getErrorsByLayerAndLevel,
  getFlattenedValidation,
} from 'samples/plate-maps/validation/PlateMapValidationUtil';
import {
  PlateMapErrorLevel,
  PlateMapValidation,
  ValidationErrorsByLayerAndLevel,
} from 'samples/plate-maps/validation/ValidationTypes';
import StorableStore from 'samples/StorableStore';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {StepItemStatus} from 'ui-library/StepNav';

interface AnnotateFillPlateModalState {
  initialPlateMap: PlateMap | null;
  plateMapType: PlateMapType | null;
  /**
   * The container schema that corresponds to the chosen plate schema.
   *
   * NOTE: This field is effectively derived from the following sources:
   *
   * 1. PlateMapType.DESIGN
   *      plateDesignState.plateSchema.containerSchemaId
   *      via CREATE_NEW_PLATE_MAP
   * 2. PlateMapType.RECORD -> PlateDefinitionType.CREATE:
   *      createPlatePaneState.selectedSchema.containerSchemaId
   *      via CREATE_NEW_PLATE_MAP
   * 3. PlateMapType.RECORD -> PlateDefinitionType.EXISTING
   *      selectedGrid.storableSchema.containerSchemaId
   *      via UPDATE_INITIAL_PLATE_MAP
   */
  containerSchemaId: string | null;
  stepKey: AnnotateFillPlateModalStep;
  statusByStepKey: Record<AnnotateFillPlateModalStep, StepItemStatus>;
  /** Only relevant if plateMapType == PlateMapType.RECORD */
  plateDefinitionType: PlateDefinitionType;
  createPlatePaneState: CreatePlatePaneState;
  existingPlateState: DefineExistingPlateState;
  plateDesignState: PlateDesignState;
  isCreatingPlateRecordFromDesign: boolean;
  selectedGrid: FullyAccessibleGrid | null;
  dismissablePlateMapValidation: ValidationErrorsByLayerAndLevel;
}

export const DEFAULT_DEFINE_EXISTING_PLATE_STATE: DefineExistingPlateState = {
  selectedGridId: null,
  isValidating: false,
  error: null,
  tags: [],
  appendStudyIds: [],
};

export const DEFAULT_PLATE_DESIGN_STATE: PlateDesignState = {
  plateSchema: null,
};

export const DEFAULT_ANNOTATE_FILL_PLATE_MODAL_STATE: AnnotateFillPlateModalState = {
  initialPlateMap: null,
  plateMapType: null,
  containerSchemaId: null,
  stepKey: AnnotateFillPlateModalStep.DEFINE_PLATE,
  statusByStepKey: {
    [AnnotateFillPlateModalStep.DEFINE_PLATE]: StepItemStatus.UNVISITED,
    [AnnotateFillPlateModalStep.DEFINE_PLATE_MAP]: StepItemStatus.UNVISITED,
    [AnnotateFillPlateModalStep.TRANSFER_QUANTITIES]: StepItemStatus.UNVISITED,
  },
  plateDefinitionType: PlateDefinitionType.CREATE,
  createPlatePaneState: DEFAULT_CREATE_PLATE_PANE_STATE,
  existingPlateState: DEFAULT_DEFINE_EXISTING_PLATE_STATE,
  plateDesignState: DEFAULT_PLATE_DESIGN_STATE,
  isCreatingPlateRecordFromDesign: false,
  selectedGrid: null,
  dismissablePlateMapValidation: {},
};

export enum AnnotateFillPlateModalReducerActionType {
  UPDATE_INITIAL_PLATE_MAP = 'UPDATE_INITIAL_PLATE_MAP',
  CREATE_NEW_PLATE_MAP = 'CREATE_NEW_PLATE_MAP',
  UPDATE_STEP_KEY = 'UPDATE_STEP_KEY',
  UPDATE_PLATE_DEFINITION_TYPE = 'UPDATE_PLATE_DEFINITION_TYPE',
  UPDATE_PLATE_MAP_TYPE = 'UPDATE_PLATE_MAP_TYPE',
  UPDATE_CREATE_PLATE_PANE_STATE = 'UPDATE_CREATE_PLATE_PANE_STATE',
  UPDATE_EXISTING_PLATE_STATE = 'UPDATE_EXISTING_PLATE_STATE',
  UPDATE_PLATE_DESIGN_STATE = 'UPDATE_PLATE_DESIGN_STATE',
  DISMISS_VALIDATION_ERROR = 'DISMISS_VALIDATION_ERROR',
}

interface AnnotateFillPlateModalAction {
  type: AnnotateFillPlateModalReducerActionType;
}

interface UpdateInitialPlateMapAction extends AnnotateFillPlateModalAction {
  type: AnnotateFillPlateModalReducerActionType.UPDATE_INITIAL_PLATE_MAP;
  updatedPlateMapInfo: {
    plateMap: PlateMap;
    containerSchemaId: string;
  } | null;
  isInProcedure: boolean;
  plateDataParameters: Array<PlateDataParameter>;
}

interface UpdateStepKeyAction extends AnnotateFillPlateModalAction {
  type: AnnotateFillPlateModalReducerActionType.UPDATE_STEP_KEY;
  stepKey: AnnotateFillPlateModalStep;
}

interface CreateNewPlateMapAction extends AnnotateFillPlateModalAction {
  type: AnnotateFillPlateModalReducerActionType.CREATE_NEW_PLATE_MAP;
  containerSchemaId: string;
  containerSchemaFields: Array<SchemaField>;
  plateDataParameters?: Array<PlateDataParameter>;
}

interface UpdatePlateMapTypeAction extends AnnotateFillPlateModalAction {
  type: AnnotateFillPlateModalReducerActionType.UPDATE_PLATE_MAP_TYPE;
  plateMapType: PlateMapType;
}

interface UpdatePlateDefinitionTypeAction extends AnnotateFillPlateModalAction {
  type: AnnotateFillPlateModalReducerActionType.UPDATE_PLATE_DEFINITION_TYPE;
  plateDefinitionType: PlateDefinitionType;
}

interface UpdateCreatePlatePaneStateAction extends AnnotateFillPlateModalAction {
  type: AnnotateFillPlateModalReducerActionType.UPDATE_CREATE_PLATE_PANE_STATE;
  createPlatePaneState: CreatePlatePaneState;
}

interface UpdateExistingPlateStateAction extends AnnotateFillPlateModalAction {
  type: AnnotateFillPlateModalReducerActionType.UPDATE_EXISTING_PLATE_STATE;
  existingPlateState: DefineExistingPlateState;
}

interface UpdatePlateDesignStateAction extends AnnotateFillPlateModalAction {
  type: AnnotateFillPlateModalReducerActionType.UPDATE_PLATE_DESIGN_STATE;
  plateDesignState: PlateDesignState;
}

interface DismissValidationErrorAction extends AnnotateFillPlateModalAction {
  type: AnnotateFillPlateModalReducerActionType.DISMISS_VALIDATION_ERROR;
  errorUuids: Set<string>;
}

export type AnnotateFillPlateModalReducerAction =
  | UpdateInitialPlateMapAction
  | UpdateStepKeyAction
  | CreateNewPlateMapAction
  | UpdatePlateDefinitionTypeAction
  | UpdateCreatePlatePaneStateAction
  | UpdateExistingPlateStateAction
  | UpdatePlateDesignStateAction
  | UpdatePlateMapTypeAction
  | DismissValidationErrorAction;

/**
 * Reset form state to support switching between the 3 modes of the modal:
 *
 * 1. PlateMapType.DESIGN
 * 2. PlateMapType.RECORD -> PlateDefinitionType.EXISTING
 * 3. PlateMapType.RECORD -> PlateDefinitionType.CREATE
 *
 * NB: We could do more targeted reset where we preserve analogous state between
 * modes, but for now, we reset mostly everything... apart from the registry, studies, and plate schema that
 * were selected for PlateDefinitionType.CREATE. Studies is preserved because it can be seeded via
 * initialStudyIds.
 */
function resetDesignCreateExistingPlateStates(
  prevState: AnnotateFillPlateModalState
): AnnotateFillPlateModalState {
  const resetState: AnnotateFillPlateModalState = {
    ...prevState,
    createPlatePaneState: {
      ...DEFAULT_CREATE_PLATE_PANE_STATE,
      selectedSchema: prevState.createPlatePaneState.selectedSchema,
      selectedContainerSchema: prevState.createPlatePaneState.selectedContainerSchema,
      registry: prevState.createPlatePaneState.registry,
      studyIds: prevState.createPlatePaneState.studyIds,
    },
    existingPlateState: {
      ...DEFAULT_DEFINE_EXISTING_PLATE_STATE,
      appendStudyIds: prevState.existingPlateState.appendStudyIds,
    },
    selectedGrid: null,
  };

  if (prevState.isCreatingPlateRecordFromDesign) {
    return resetState;
  } else {
    return {
      ...resetState,
      initialPlateMap: null,
      containerSchemaId: null,
      plateDesignState: DEFAULT_PLATE_DESIGN_STATE,
    };
  }
}

/**
 * Reducer function for useAnnotateFillPlateModalReducer.
 * @param prevState The state of the modal before the action was taken.
 * @param action An action being taken in the UI, and any metadata necessary to update the state.
 */
function reducer(
  prevState: AnnotateFillPlateModalState,
  action: AnnotateFillPlateModalReducerAction
): AnnotateFillPlateModalState {
  switch (action.type) {
    case AnnotateFillPlateModalReducerActionType.UPDATE_PLATE_MAP_TYPE: {
      if (prevState.plateMapType === action.plateMapType) {
        return prevState;
      }
      return {
        ...resetDesignCreateExistingPlateStates(prevState),
        plateMapType: action.plateMapType,
      };
    }
    case AnnotateFillPlateModalReducerActionType.UPDATE_INITIAL_PLATE_MAP: {
      const initialSelectedDataParameters = getInitialSelectedDataParameters(action.plateDataParameters);

      let initialPlateMap: PlateMap | null;
      if (action.updatedPlateMapInfo?.plateMap != null) {
        initialPlateMap = produce(action.updatedPlateMapInfo.plateMap, (draft) => {
          draft.wells.forEach((well) => {
            well.dataParameterTags = getInitialDataParameterTags(initialSelectedDataParameters);
            if (action.isInProcedure) {
              well.contents.forEach((content) => {
                content.materialInput = {materialInputId: NO_MATERIAL_INPUT_SENTINEL, error: null};
              });
            }
          });
          draft.dataParameterNamesByVersionId = getDataParameterNamesByVersionId(action.plateDataParameters);
        });
      } else {
        initialPlateMap = null;
      }
      return {
        ...prevState,
        initialPlateMap,
        containerSchemaId: action.updatedPlateMapInfo?.containerSchemaId ?? null,
      };
    }
    case AnnotateFillPlateModalReducerActionType.UPDATE_STEP_KEY: {
      const modalSteps = ANNOTATE_FILL_PLATE_MODAL_STEPS;
      const newStepIndex = modalSteps.findIndex((step) => step.key === action.stepKey);
      return produce(prevState, (draft) => {
        draft.stepKey = action.stepKey;
        modalSteps.forEach((step, index) => {
          draft.statusByStepKey[step.key] =
            index < newStepIndex ? StepItemStatus.DONE : StepItemStatus.UNVISITED;
        });
      });
    }
    case AnnotateFillPlateModalReducerActionType.CREATE_NEW_PLATE_MAP: {
      // If there is not already a plate map specified, create a new empty one.
      if (prevState.initialPlateMap == null) {
        const plateSchema =
          prevState.plateMapType === PlateMapType.DESIGN
            ? prevState.plateDesignState.plateSchema
            : prevState.plateMapType === PlateMapType.RECORD
              ? prevState.createPlatePaneState.selectedSchema
              : null;
        assert(plateSchema != null, 'Expected plate schema to be selected before CREATE_NEW_PLATE_MAP.', {
          plateMapType: prevState.plateMapType,
        });
        return {
          ...prevState,
          initialPlateMap: getEmptyPlateMap(
            plateSchema,
            action.containerSchemaFields,
            action.plateDataParameters
          ),
          containerSchemaId: action.containerSchemaId,
        };
      } else {
        return prevState;
      }
    }
    case AnnotateFillPlateModalReducerActionType.UPDATE_PLATE_DEFINITION_TYPE: {
      if (prevState.plateDefinitionType === action.plateDefinitionType) {
        return prevState;
      }
      return {
        ...resetDesignCreateExistingPlateStates(prevState),
        plateDefinitionType: action.plateDefinitionType,
      };
    }
    case AnnotateFillPlateModalReducerActionType.UPDATE_CREATE_PLATE_PANE_STATE: {
      return produce(prevState, (draft) => {
        draft.createPlatePaneState = action.createPlatePaneState;
        // If a plate map was previously created but the schema changed, clear the plate map.
        if (
          prevState.initialPlateMap != null &&
          prevState.createPlatePaneState.selectedSchema?.id !== action.createPlatePaneState.selectedSchema?.id
        ) {
          draft.initialPlateMap = null;
          draft.containerSchemaId = null;
        }
      });
    }
    case AnnotateFillPlateModalReducerActionType.UPDATE_EXISTING_PLATE_STATE: {
      return produce(prevState, (draft) => {
        draft.existingPlateState = action.existingPlateState;
        if (
          action.existingPlateState.selectedGridId != null &&
          !action.existingPlateState.isValidating &&
          action.existingPlateState.error == null
        ) {
          draft.selectedGrid = StorableStore.getGrid<FullyAccessibleGrid>(
            action.existingPlateState.selectedGridId
          );
        } else if (action.existingPlateState.selectedGridId == null) {
          draft.selectedGrid = null;
        }
      });
    }
    case AnnotateFillPlateModalReducerActionType.UPDATE_PLATE_DESIGN_STATE: {
      return produce(prevState, (draft) => {
        draft.plateDesignState = action.plateDesignState;
        // If a plate map was previously created but the schema changed, clear the plate map.
        if (
          prevState.initialPlateMap != null &&
          prevState.plateDesignState.plateSchema?.id !== action.plateDesignState.plateSchema?.id
        ) {
          draft.initialPlateMap = null;
          draft.containerSchemaId = null;
        }
      });
    }
    case AnnotateFillPlateModalReducerActionType.DISMISS_VALIDATION_ERROR: {
      return {
        ...prevState,
        dismissablePlateMapValidation: filterGroupedValidation(
          prevState.dismissablePlateMapValidation,
          (errorWithUuid) => !action.errorUuids.has(errorWithUuid.uuid)
        ),
      };
    }
    default:
      return prevState;
  }
}

export type AnnotateFillPlateModalReducerDispatch = Dispatch<
  ReducerAction<Reducer<AnnotateFillPlateModalState, AnnotateFillPlateModalReducerAction>>
>;

/**
 * Handles the modal steps for the annotate and fill plate modal and the state for defining
 * a plate and fetching or creating the corresponding plate map for it.
 *
 * Actions for the annotating and filling steps are handled through a separate reducer shared with
 * the fill plate modal.
 *
 * @param initialState convenience for setting initial state in tests.
 * @param initialStudyIds should not be used in conjunction with initialState.
 * @param isInProcedure whether this modal is being launched from a procedure.
 * @param plateDataParameters if this modal is being launched from a procedure, all data parameters
 *                            associated with said procedure
 */
export default function useAnnotateFillPlateModalReducer({
  initialState,
  initialStudyIds,
  isInProcedure = false,
  plateDataParameters = [],
  initialPlateDesignState = null,
  initialPlateMap = null,
  initialPlateMapType = null,
  initialDismissablePlateMapValidation = null,
  procedureStudyDesignType = null,
}: {
  initialState?: AnnotateFillPlateModalState;
  isInProcedure?: boolean;
  initialStudyIds?: Array<string>;
  plateDataParameters?: Array<PlateDataParameter>;
  initialPlateDesignState?: PlateDesignState | null;
  initialPlateMap?: PlateMap | null;
  initialPlateMapType?: PlateMapType | null;
  initialDismissablePlateMapValidation?: PlateMapValidation | null;
  procedureStudyDesignType?: StudyDesignType | null;
}): [AnnotateFillPlateModalState, AnnotateFillPlateModalReducerDispatch] {
  const initialRegistry = useMemo(() => {
    const allRegistries = NavStore.getRegistries();
    // If we're coming from a plate design, we already have a schema, so we already know the correct
    // registry.
    if (initialPlateDesignState != null && initialPlateDesignState.plateSchema != null) {
      return allRegistries.find(
        (registry) => registry.id === initialPlateDesignState.plateSchema!.registryId
      )!;
    }
    return getInitialRegistry(allRegistries);
  }, [initialPlateDesignState]);
  let plateMapType: PlateMapType | null;
  if (initialPlateMapType == null) {
    if (initialPlateDesignState != null) {
      plateMapType = PlateMapType.DESIGN;
    } else if (BenchlingConfig.ELN_ENABLE_PLATE_DESIGNS) {
      plateMapType = null;
    } else {
      plateMapType = PlateMapType.RECORD;
    }
  } else {
    plateMapType = initialPlateMapType;
  }
  // We shouldn't be getting any non-dismissable errors back from plate map validation, but strip them out
  // anyways.
  const initialWarnings: PlateMapValidation =
    initialDismissablePlateMapValidation == null
      ? {
          layerValidation: [],
          wellValidation: [],
          fieldValueValidation: [],
          reagentValidation: [],
        }
      : filterValidation(
          initialDismissablePlateMapValidation,
          (error) => error.level === PlateMapErrorLevel.WARNING
        );
  const initialValidationLength =
    initialDismissablePlateMapValidation != null
      ? getFlattenedValidation(initialDismissablePlateMapValidation).length
      : 0;
  const filteredLength = getFlattenedValidation(initialWarnings).length;
  assertAndContinue(
    initialValidationLength === filteredLength,
    'Unexpected non-dismissable error in staleness validation response'
  );
  const dismissablePlateMapValidation = getErrorsByLayerAndLevel(initialWarnings);
  const defaultState: AnnotateFillPlateModalState = {
    ...DEFAULT_ANNOTATE_FILL_PLATE_MODAL_STATE,
    plateMapType,
    existingPlateState: {
      ...DEFAULT_ANNOTATE_FILL_PLATE_MODAL_STATE.existingPlateState,
      appendStudyIds: initialStudyIds ?? [],
    },
    createPlatePaneState: {
      ...DEFAULT_ANNOTATE_FILL_PLATE_MODAL_STATE.createPlatePaneState,
      studyIds: initialStudyIds ?? [],
      registry: initialRegistry,
      selectedSchema: initialPlateDesignState?.plateSchema ?? null,
    },
    plateDesignState: initialPlateDesignState ?? DEFAULT_PLATE_DESIGN_STATE,
    initialPlateMap: initialPlateMap ?? null,
    containerSchemaId: initialPlateDesignState?.plateSchema?.containerSchemaId ?? null,
    isCreatingPlateRecordFromDesign: initialPlateDesignState != null && plateMapType === PlateMapType.RECORD,
    dismissablePlateMapValidation,
  };
  const [annotateFillPlateModalState, dispatch] = useReducer(reducer, initialState ?? defaultState);

  const fetchTestOrders = shouldShowTestOrderDilutionInfo(procedureStudyDesignType, plateMapType);
  const {plateMap, isPending} = usePlateMapFromGrid(
    annotateFillPlateModalState.selectedGrid,
    fetchTestOrders
  );

  useEffect(() => {
    if (
      defaultState.initialPlateMap == null &&
      annotateFillPlateModalState.plateMapType === PlateMapType.RECORD &&
      annotateFillPlateModalState.plateDefinitionType === PlateDefinitionType.EXISTING &&
      plateMap?.id !== annotateFillPlateModalState.initialPlateMap?.id &&
      !isPending
    ) {
      dispatch({
        type: AnnotateFillPlateModalReducerActionType.UPDATE_INITIAL_PLATE_MAP,
        updatedPlateMapInfo:
          plateMap != null && annotateFillPlateModalState.selectedGrid != null
            ? {
                plateMap,
                containerSchemaId: annotateFillPlateModalState.selectedGrid.storableSchema.containerSchemaId!,
              }
            : null,
        isInProcedure,
        plateDataParameters,
      });
    }
  }, [
    annotateFillPlateModalState.initialPlateMap?.id,
    annotateFillPlateModalState.plateMapType,
    annotateFillPlateModalState.plateDefinitionType,
    annotateFillPlateModalState.selectedGrid,
    isInProcedure,
    isPending,
    plateMap,
    plateDataParameters,
    defaultState.initialPlateMap,
  ]);

  return [annotateFillPlateModalState, dispatch];
}
